import LinkOffRounded from '@mui/icons-material/LinkOffRounded';
import QrCode2Rounded from '@mui/icons-material/QrCode2Rounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { businessDate, dateTime } from '../../lib/format';
import { errorMessage, useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { useWhatsappAdmin, useWhatsappStatus } from './api';
import { WhatsappMessages } from './WhatsappMessages';
import { WHATSAPP_GREEN } from './WhatsappSend';

/**
 * Settings → WhatsApp: link the shop's one WhatsApp number by scanning a QR code (WhatsApp >
 * Linked devices), see which number is linked, unlink it, and the country code for local numbers.
 */
export function WhatsappTab() {
  const { can } = useAuth();
  const { format } = useShop();
  const notify = useNotify();
  const [polling, setPolling] = useState(false);
  const status = useWhatsappStatus(polling);
  const admin = useWhatsappAdmin();
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const canManage = can('whatsapp.manage');
  const plugin = status.data?.plugin;
  const connection = status.data?.connection;
  const linkedNow = connection?.status === 'LINKED';
  // Polls while linking (it stops once linked); then say so.
  useEffect(() => {
    if (polling && linkedNow) notify.success('WhatsApp linked');
  }, [polling, linkedNow, notify]);

  if (status.isLoading) return <CircularProgress />;
  if (!plugin || plugin.state === 'NOT_ENABLED')
    return (
      <Alert severity="info">
        WhatsApp is an add-on. See the Subscription page to ask PackBytes for it.
      </Alert>
    );

  const linked = connection?.status === 'LINKED';
  const pairing = connection?.status === 'PAIRING';

  return (
    <Stack spacing={2.5}>
      {plugin.state === 'EXPIRED' && (
        <Alert severity="error">
          WhatsApp subscription ended, grace used. Contact PackBytes to renew it.
        </Alert>
      )}
      {plugin.state === 'GRACE' && plugin.graceEndsAt && (
        <Alert severity="warning">
          The WhatsApp period has ended; it keeps working until{' '}
          {businessDate(plugin.graceEndsAt.slice(0, 10), format)}. Contact PackBytes to renew it.
        </Alert>
      )}
      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 2 }}>
            <WhatsAppIcon sx={{ color: WHATSAPP_GREEN, fontSize: 32 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">WhatsApp number</Typography>
              <Typography variant="body2" color="text.secondary">
                One number for the shop. Documents are sent from it.
              </Typography>
            </Box>
          </Stack>
          {linked ? (
            <Stack spacing={1.5}>
              <Typography>
                Linked: <strong>+{connection.phone}</strong>
                {connection.displayName ? ` (${connection.displayName})` : ''}
              </Typography>
              {connection.linkedAt && (
                <Typography variant="body2" color="text.secondary">
                  Since {dateTime(connection.linkedAt, format)}
                </Typography>
              )}
              {canManage && (
                <Box>
                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={<LinkOffRounded />}
                    onClick={() => setConfirmUnlink(true)}
                  >
                    Unlink
                  </Button>
                </Box>
              )}
            </Stack>
          ) : pairing && connection.qr ? (
            <Stack spacing={2} sx={{ alignItems: { sm: 'flex-start' } }}>
              <Box
                component="img"
                src={connection.qr}
                alt="WhatsApp link QR code"
                sx={{ width: 260, height: 260, bgcolor: '#fff', p: 1, borderRadius: 2 }}
              />
              <Typography variant="body2">
                On the shop’s phone open WhatsApp → <strong>Linked devices</strong> →{' '}
                <strong>Link a device</strong>, then scan this code. It refreshes by itself.
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
              {connection?.lastError && <Alert severity="info">{connection.lastError}</Alert>}
              <Typography variant="body2">No number is linked yet.</Typography>
              {canManage && (
                <Button
                  variant="contained"
                  startIcon={<QrCode2Rounded />}
                  disabled={admin.connect.isPending || plugin.state === 'EXPIRED'}
                  onClick={() =>
                    admin.connect.mutate(undefined, {
                      onSuccess: () => setPolling(true),
                      onError: (e) => notify.error(errorMessage(e)),
                    })
                  }
                  sx={{ bgcolor: WHATSAPP_GREEN, '&:hover': { bgcolor: '#178A43' } }}
                >
                  {admin.connect.isPending ? 'Getting the QR code…' : 'Link WhatsApp'}
                </Button>
              )}
            </Stack>
          )}
          <Alert severity="info" variant="outlined" sx={{ mt: 2.5 }}>
            WhatsApp may block numbers that send many messages to people who did not ask for them.
            Send documents to your own customers and suppliers only.
          </Alert>
        </CardContent>
      </Card>
      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Local numbers
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ alignItems: { sm: 'center' } }}
          >
            <TextField
              label="Country calling code"
              value={code ?? plugin.countryCode}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
              helperText="Numbers starting with 0 get this code, e.g. 0300… → +92 300…"
              disabled={!canManage}
              slotProps={{ input: { startAdornment: <Typography sx={{ mr: 0.5 }}>+</Typography> } }}
              sx={{ maxWidth: 280 }}
            />
            {canManage && code !== null && code !== plugin.countryCode && (
              <Button
                variant="contained"
                disabled={!code || admin.settings.isPending}
                onClick={() =>
                  admin.settings.mutate(
                    { countryCode: code, version: plugin.version ?? 1 },
                    {
                      onSuccess: () => {
                        setCode(null);
                        notify.success('Saved');
                      },
                      onError: (e) => notify.error(errorMessage(e)),
                    },
                  )
                }
              >
                Save
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
      <WhatsappMessages plugin={plugin} canManage={canManage} />
      <Dialog open={confirmUnlink} onClose={() => setConfirmUnlink(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Unlink WhatsApp?</DialogTitle>
        <DialogContent>
          <Typography>
            The shop stops sending from +{connection?.phone}. You can link a number again later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmUnlink(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={admin.disconnect.isPending}
            onClick={() =>
              admin.disconnect.mutate(undefined, {
                onSuccess: () => setConfirmUnlink(false),
                onError: (e) => notify.error(errorMessage(e)),
              })
            }
          >
            Unlink
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
